import axios from "axios";
import jwtDecode from "jwt-decode";
import { BASE_URL } from "./config";
import { clearAuth, getAccessToken, getRefreshToken, isCookieAuth, persistLogin } from "./auth";
import logger from "./utils/logger";

/**
 * Shared axios client.
 *
 * In cookie mode the browser handles credentials transparently, so we
 * just turn on `withCredentials` and let it ride. In bearer mode we
 * keep the original short-circuit refresh interceptor.
 */
const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: isCookieAuth(),
});

const isTokenExpired = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch {
    return true;
  }
};

api.interceptors.request.use(
  async (config) => {
    if (isCookieAuth()) {
      // Cookie auth: nothing to attach. Browser will send the session
      // cookie because `withCredentials` is on at the instance level.
      return config;
    }

    let token = getAccessToken();
    const refreshToken = getRefreshToken();

    if (isTokenExpired(token) && refreshToken) {
      try {
        const response = await axios.post(`${BASE_URL}/refresh-jwt-token`, {
          refreshToken,
        });
        // Body is `{data: {accessToken, refreshToken}}` (see
        // project/apis/tokens/routes.py) — reading the fields off
        // `response.data` directly (skipping the `data` envelope) silently
        // persisted `undefined`, so the stale/expired token in localStorage
        // was never replaced and every request kept 401ing after the
        // access token's first expiry.
        const { accessToken, refreshToken: newRefreshToken } = response.data?.data ?? {};
        persistLogin({
          accessToken,
          refreshToken: newRefreshToken,
        });
        token = accessToken;
      } catch (error) {
        logger.error("Failed to refresh token", error);
        clearAuth();
        if (window.location.pathname !== "/") window.location.href = "/";
        return Promise.reject(error);
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

export default api;
